import { useCallback, useRef, useState } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import useCostCentersDistribution from 'modules/cost_centers/hooks/useCostCentersDistribution'
import { useCostCentersFeatureEnabled } from 'modules/cost_centers/hooks/useFeatureEnabled'
import {
  AllocationReference,
  AllocationSelectOption,
  AssignmentsChange,
  ResourceDistribution,
} from 'modules/cost_centers/lib/types'

interface Props {
  resourceType: string
  resourceId?: number
  referenceResourceId?: number
  defaultSelectOption?: string
  options: AllocationSelectOption[]
  distribution?: ResourceDistribution
  onChange?: (
    value: { assignments: AssignmentsChange } | { allocation: AllocationReference }
  ) => void
  loading: boolean
}

export const useCostCenterAllocation = ({
  resourceType,
  resourceId,
  referenceResourceId,
  defaultSelectOption,
  options,
  distribution,
  loading,
  onChange,
}: Props) => {
  const [selectValue, setSelectValue] = useState<string>()
  const [hasInitialized, setHasInitialized] = useState(false)
  const isFeatureEnabled = useCostCentersFeatureEnabled()

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const { distribution: resourceDistribution, isLoading: loadingResourceDistribution } =
    useCostCentersDistribution({
      resourceType,
      resourceId: Number(resourceId),
      skipQueries: !resourceId || !resourceType || !isFeatureEnabled || !!distribution,
    })

  const selectedOption = options.find((option) => option.value === selectValue)
  const selectedOptionIsResource = selectedOption?.value === resourceType
  const currentDistribution = distribution ?? resourceDistribution
  const savedAllocationId =
    currentDistribution.find((value) => !!value.allocationId)?.allocationId ?? undefined

  const handleAllocationChange = useCallback(
    (newValue: string) => {
      if (newValue !== resourceType) {
        const findOption = options.find((option) => option.value === newValue)

        onChange?.({
          allocation: {
            id: savedAllocationId,
            referenceType: String(findOption?.value),
            referenceId: Number(findOption?.resourceId),
          },
        })
      }
    },
    [onChange, resourceType, options]
  )

  const handleSelectChange = useCallback(
    (newValue: string) => {
      setSelectValue(newValue)
      handleAllocationChange(newValue)
    },
    [handleAllocationChange]
  )

  const notifyChange = (newValue: string) => {
    const valueInOptions = options.find((option) => option.value === newValue)

    if (newValue !== resourceType && valueInOptions) {
      const findOption = options.find((option) => option.value === newValue)

      onChangeRef.current?.({
        allocation: {
          id: savedAllocationId,
          referenceType: String(findOption?.value),
          referenceId: Number(findOption?.resourceId),
        },
      })
    }
  }

  useUnsafeEffect(() => {
    if (loadingResourceDistribution || loading || !hasInitialized) return

    const validSelectedOption = options.find((option) => option.value === selectValue)

    if (validSelectedOption) {
      handleAllocationChange(validSelectedOption.value)
    }
  }, [referenceResourceId, hasInitialized, loadingResourceDistribution, loading, selectValue])

  useUnsafeEffect(() => {
    if (loadingResourceDistribution || loading || hasInitialized) return

    if (!currentDistribution.length) {
      const defaultValue = defaultSelectOption || resourceType || ''
      setSelectValue(defaultValue)
      notifyChange(defaultValue)
    } else if (currentDistribution[0]?.allocationReferenceType) {
      const allocationType = currentDistribution[0].allocationReferenceType
      setSelectValue(allocationType)
      notifyChange(allocationType)
    } else {
      setSelectValue(resourceType)
    }

    setHasInitialized(true)
  }, [loadingResourceDistribution, loading])

  return {
    selectValue,
    selectedOption,
    selectedOptionIsResource,
    resourceDistribution: currentDistribution,
    loadingResourceDistribution,
    isFeatureEnabled,
    handleSelectChange,
  }
}
